import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import AdminSortingCriteriaService, {
    CreatePayload,
    DeletePayload,
    UpdatePayload,
    UpdateValuePayload,
} from '../services/AdminSortingCriteriaService';
import CriterionType from '../types/criteria/CriterionType';
import CriterionValueType from '../types/criteria/CriterionValueType';
import { RequestServiceResponse } from '@tcc-online/request-service';
import ImportStates, { ImportState } from '../constants/ImportStates';
import CriteriaImportPreview from '../types/criteria/CriteriaImportPreview';
import ImportProcess from '../../common/types/ImportProcess';
import ImportService from '../services/ImportService';

const NAME = 'AdminSortingCriteria';

const getCriteria = createAsyncThunk(
    `${NAME}/getCriteria`,
    AdminSortingCriteriaService.getCriteria,
);
const getCriterion = createAsyncThunk(
    `${NAME}/getCriterion`,
    AdminSortingCriteriaService.getCriterion,
);
const updateCriterion = createAsyncThunk<
    RequestServiceResponse<CriterionType>,
    UpdatePayload
>(`${NAME}/updateCriterion`, async (payload, thunkAPI) => {
    const { parentCriterionId } = payload;
    const result = await AdminSortingCriteriaService.updateCriterion(
        payload,
        thunkAPI,
    );

    if (parentCriterionId) {
        const { projectId } = payload;
        thunkAPI.dispatch(
            AdminSortingCriteriaSlice.actions.getValues({
                projectId,
                criterionId: parentCriterionId,
            }),
        );
    }

    return result;
});
const moveCriterion = createAsyncThunk<
    RequestServiceResponse<CriterionType>,
    UpdatePayload
>(`${NAME}/moveCriterion`, async (payload, thunkAPI) => {
    const { parentCriterionId } = payload;
    const result = await AdminSortingCriteriaService.updateCriterion(
        payload,
        thunkAPI,
    );
    const { projectId } = payload;
    if (parentCriterionId) {
        thunkAPI.dispatch(
            AdminSortingCriteriaSlice.actions.getValues({
                projectId,
                criterionId: parentCriterionId,
            }),
        );
    } else {
        thunkAPI.dispatch(
            AdminSortingCriteriaSlice.actions.getCriteria({
                projectId,
            }),
        );
    }

    return result;
});
const createCriterion = createAsyncThunk<
    RequestServiceResponse<CriterionType>,
    CreatePayload
>(`${NAME}/createCriterion`, async (payload, thunkAPI) => {
    const { parentCriterionId } = payload;
    const result = await AdminSortingCriteriaService.createCriterion(
        payload,
        thunkAPI,
    );

    if (parentCriterionId) {
        const { projectId, parentCriterionValue } = payload;
        thunkAPI.dispatch(
            AdminSortingCriteriaSlice.actions.getValues({
                projectId,
                criterionId: parentCriterionId,
                parentCriterionValueId: parentCriterionValue?.id,
            }),
        );
    }

    return result;
});
const deleteCriterion = createAsyncThunk<
    RequestServiceResponse<CriterionType>,
    DeletePayload
>(`${NAME}/deleteCriterion`, async (payload, thunkAPI) => {
    const { parentCriterionId } = payload;
    const result = await AdminSortingCriteriaService.deleteCriterion(
        payload,
        thunkAPI,
    );

    if (parentCriterionId) {
        const { projectId } = payload;
        thunkAPI.dispatch(
            AdminSortingCriteriaSlice.actions.getValues({
                projectId,
                criterionId: parentCriterionId,
            }),
        );
    }

    return result;
});

const getValues = createAsyncThunk(
    `${NAME}/getValues`,
    AdminSortingCriteriaService.getValues,
);
const updateValue = createAsyncThunk(
    `${NAME}/updateValue`,
    AdminSortingCriteriaService.updateValue,
);
const moveValue = createAsyncThunk<
    RequestServiceResponse<CriterionValueType>,
    UpdateValuePayload
>(`${NAME}/moveValue`, async (payload, thunkAPI) => {
    const result = await AdminSortingCriteriaService.updateValue(
        payload,
        thunkAPI,
    );
    const { projectId, criterionId } = payload;

    thunkAPI.dispatch(
        AdminSortingCriteriaSlice.actions.getCriterion({
            projectId,
            id: criterionId,
        }),
    );

    return result;
});
const createValue = createAsyncThunk(
    `${NAME}/createValue`,
    AdminSortingCriteriaService.createValue,
);
const deleteValue = createAsyncThunk(
    `${NAME}/deleteValue`,
    AdminSortingCriteriaService.deleteValue,
);

const importCriteria = createAsyncThunk(
    `${NAME}/importCriteria`,
    AdminSortingCriteriaService.importCriteria,
);
const confirmImport = createAsyncThunk(
    `${NAME}/confirmImport`,
    ImportService.confirmImport,
);
const cancelImport = createAsyncThunk(
    `${NAME}/cancelImport`,
    ImportService.cancelImport,
);
const getImportProgress = createAsyncThunk(
    `${NAME}/getImportProgress`,
    ImportService.getImportProgress,
);
const exportCriteria = createAsyncThunk(
    `${NAME}/exportCriteria`,
    AdminSortingCriteriaService.exportCriteria,
);

export interface AdminSortingCriteriaState {
    criteria: IBaseState<CriterionType[]>;
    import: {
        loading: boolean;
        preview: CriteriaImportPreview | null;
        process: ImportProcess | null;
        state: ImportState;
        importId: number | null;
        projectId: number | null;
    };
}

const initialState: AdminSortingCriteriaState = {
    criteria: {
        data: [],
        loading: false,
    },
    import: {
        loading: false,
        preview: null,
        process: null,
        state: ImportStates.Idle,
        importId: ImportService.getImportId(),
        projectId: null,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {
        resetImport,
    },
    extraReducers: (builder) => {
        builder.addCase(
            getCriteria.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (action.payload.data) {
                    state.criteria.data = action.payload.data;
                }
            },
        );
        // builder.addCase(getCriterion.fulfilled, (state:AdminSortingCriteriaState, action) => {
        //     if (!action.payload.data) {
        //         return;
        //     }
        //
        //     const criterion = findCriterionById();
        //
        //
        // })
        builder.addCase(
            createCriterion.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const data = action.payload.data;

                const { parentCriterionValueId } = action.meta.arg;
                if (!parentCriterionValueId) {
                    state.criteria.data.push(data);
                    return;
                }

                // const [value] = findValueById(
                //     state.data,
                //     parentCriterionValueId,
                // );
                // if (!value) {
                //     return;
                // }
                //
                // if (!value.criteria) {
                //     value.criteria = [];
                // }
                //
                // value.criteria.push(data);
            },
        );
        builder.addCase(
            updateCriterion.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const { id, parentCriterionId } = action.meta.arg;

                if (parentCriterionId) {
                    return;
                }

                const criterion = findCriterionById(state.criteria.data, id);
                if (!criterion) {
                    return;
                }

                const { values, name, mandatory, displayOption, order } =
                    action.payload.data;

                criterion.values = values;
                criterion.name = name;
                criterion.mandatory = mandatory;
                criterion.displayOption = displayOption;
                criterion.order = order;
            },
        );
        // builder.addCase(
        //     deleteCriterion.fulfilled,
        //     (state: AdminSortingCriteriaState, action) => {
        //         const { id } = action.meta.arg;
        //
        //         let [criterion, criteria, index] = findCriterionById(
        //             state.data,
        //             id,
        //         );
        //         if (!criterion || !criteria.length) {
        //             return;
        //         }
        //
        //         criteria.splice(index, 1);
        //     },
        // );

        builder.addCase(
            getValues.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const { criterionId } = action.meta.arg;
                if (!criterionId) {
                    return;
                }

                const criterion = findCriterionById(
                    state.criteria.data,
                    criterionId,
                );
                if (!criterion) {
                    return;
                }

                criterion.values = action.payload.data;
            },
        );
        builder.addCase(
            createValue.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const { criterionId } = action.meta.arg;
                if (!criterionId) {
                    return;
                }
                const criterion = findCriterionById(
                    state.criteria.data,
                    criterionId,
                );
                if (!criterion) {
                    return;
                }

                if (!criterion.values) {
                    criterion.values = [];
                }

                criterion.values.push(action.payload.data);
            },
        );
        builder.addCase(
            updateValue.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                if (!action.payload.data) {
                    return;
                }

                const { id } = action.meta.arg;

                const [criterionValue] = findValueById(state.criteria.data, id);
                if (!criterionValue) {
                    return;
                }

                const { value, criteria, order } = action.payload.data;

                criterionValue.value = value;
                criterionValue.criteria = criteria;
                criterionValue.order = order;
            },
        );
        builder.addCase(
            deleteValue.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                const { id } = action.meta.arg;

                const [value, values, index] = findValueById(
                    state.criteria.data,
                    id,
                );
                if (!value || !values.length) {
                    return;
                }

                values.splice(index, 1);
            },
        );

        builder.addCase(
            importCriteria.pending,
            (state: AdminSortingCriteriaState, action) => {
                resetImport(state);
                state.import.state = ImportStates.Pending;
                state.import.projectId = action.meta.arg.projectId;
            },
        );
        builder.addCase(
            importCriteria.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                state.import.state = ImportStates.Preview;
                state.import.importId = action.payload.data.importId;
                state.import.preview = action.payload.data.preview;
            },
        );
        builder.addCase(
            importCriteria.rejected,
            (state: AdminSortingCriteriaState) => {
                resetImport(state);
                state.import.state = ImportStates.Fail;
            },
        );
        builder.addCase(
            confirmImport.pending,
            (state: AdminSortingCriteriaState) => {
                state.import.state = ImportStates.Pending;
            },
        );
        builder.addCase(
            confirmImport.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                state.import.state = ImportStates.Importing;
                const data = action.payload.data;
                state.import.process = data;
                if (data.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            confirmImport.rejected,
            (state: AdminSortingCriteriaState) => {
                resetImport(state);
            },
        );
        builder.addCase(
            cancelImport.pending,
            (state: AdminSortingCriteriaState) => {
                state.import.state = ImportStates.Idle;
                state.import.importId = null;
            },
        );
        builder.addCase(
            getImportProgress.fulfilled,
            (state: AdminSortingCriteriaState, action) => {
                const data = action.payload.data;

                state.import.process = data;
                if (data.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            getImportProgress.rejected,
            (state: AdminSortingCriteriaState) => {
                resetImport(state);
            },
        );
    },
});

function resetImport(state: AdminSortingCriteriaState) {
    state.import.state = ImportStates.Idle;
    state.import.importId = null;
    state.import.preview = null;
    state.import.process = null;
    state.import.projectId = null;
}

function findCriterionById(criteria: CriterionType[], id: number) {
    const result: CriterionType | undefined = undefined;
    return find(criteria, id, result);

    function find(
        criteria: CriterionType[],
        id: number,
        result?: CriterionType,
    ) {
        for (let index = 0; index < criteria.length; index++) {
            if (result) {
                break;
            }

            const criterion = criteria[index];
            if (criterion.id === id) {
                result = criterion;
                break;
            }

            if (!criterion?.values?.length) {
                continue;
            }

            for (const value of criterion.values) {
                if (result) {
                    break;
                }

                if (!value?.criteria?.length) {
                    continue;
                }

                result = find(value.criteria, id, result);
            }
        }

        return result;
    }
}

function findValueById(
    criteria: CriterionType[],
    id: number,
): [CriterionValueType | null, CriterionValueType[], number] {
    const result: [CriterionValueType | null, CriterionValueType[], number] = [
        null,
        [],
        -1,
    ];

    return find(criteria, id, result);
    function find(
        criteria: CriterionType[],
        id: number,
        result: [CriterionValueType | null, CriterionValueType[], number],
    ) {
        for (const criterion of criteria) {
            if (result[0]) {
                break;
            }

            if (!criterion?.values?.length) {
                continue;
            }

            const values = criterion.values;
            for (let index = 0; index < values.length; index++) {
                if (result[0]) {
                    break;
                }

                const value = values[index];
                if (value.id === id) {
                    result = [value, criterion.values, index];
                    break;
                }

                if (!value?.criteria?.length) {
                    continue;
                }

                result = findValueById(value.criteria, id);
            }
        }

        return result;
    }
}

const state = (state: AppState): AdminSortingCriteriaState =>
    state.admin.criteria;
const all = createSelector(state, (state: AdminSortingCriteriaState) => state);
const AdminSortingCriteriaSlice = {
    reducer: slice.reducer,
    actions: {
        ...slice.actions,
        getCriteria,
        getCriterion,
        updateCriterion,
        createCriterion,
        deleteCriterion,
        moveCriterion,
        getValues,
        updateValue,
        createValue,
        deleteValue,
        moveValue,
        importCriteria,
        confirmImport,
        cancelImport,
        getImportProgress,
        exportCriteria,
    },
    state: {
        all,
        criteria: createSelector(
            all,
            (state: AdminSortingCriteriaState) => state.criteria,
        ),
        import: createSelector(
            all,
            (state: AdminSortingCriteriaState) => state.import,
        ),
    },
};

export default AdminSortingCriteriaSlice;
